import { useState, useEffect, useRef } from "react";
import { NavLink, useHistory } from "react-router-dom";
import firebase from "firebase/app";
import { db } from "../FIREBASE/firebase";
import routeUrls from "../COMPONENTS/navbar/navLink";
import { ActivityForm, LocationAutocomplete } from "../COMPONENTS/planner/ActivityForm";
import { LodgingEditor } from "../COMPONENTS/planner/LodgingEditor";
import { TripMap } from "../COMPONENTS/planner/TripMap";
import { BudgetPanel } from "../COMPONENTS/planner/BudgetPanel";
import { driveMinutes, formatDriveTime } from "../COMPONENTS/planner/route";
import { lookupFlight, lookupFlightAny, FlightLookupResult } from "../COMPONENTS/planner/flightLookup";
import {
  Activity,
  ACTIVITY_TYPES,
  BOOKING_STATUS,
  effectiveBooking,
  TripStatus,
  TRIP_STATUS,
  TRIP_STATUS_ORDER,
  tripStatusOf,
  LodgingMap,
  LodgingPoint,
  hasRoute,
  linkKind,
  byTime,
  coversDay,
  activitySpan,
  normEmail,
  isValidEmail,
  addDaysISO,
  dayCount as countDays,
  parseISO,
  emptyActivity,
  readActivities,
} from "../COMPONENTS/planner/plannerTypes";
import "../CSS/planner.scss";
import { usePageBackground } from "../COMPONENTS/usePageBackground";

type Status = "loading" | "ready" | "notfound";

// What modal (if any) is open. New activities can only be created in the idea
// pool ("add"); the calendar gets items from the pool via drag-drop or "pick".
type Editing =
  | { mode: "add" }
  | { mode: "new"; day: number } // create a brand-new activity straight onto a day
  | { mode: "edit"; id: string }
  | { mode: "view"; id: string }
  | { mode: "pick"; day: number }
  | { mode: "lodging"; day: number }
  | { mode: "day"; day: number } // tap a calendar day → what's going on
  | null;

export const PlanDetailView = ({ planId, uid, email }: { planId: string; uid: string; email: string }) => {
  usePageBackground();
  const [status, setStatus] = useState<Status>("loading");
  const [ownerUid, setOwnerUid] = useState("");
  const [sharedWith, setSharedWith] = useState<string[]>([]);
  const [shareEmail, setShareEmail] = useState("");
  const [flightNum, setFlightNum] = useState("");
  const [flightBusy, setFlightBusy] = useState(false);
  const [flightMsg, setFlightMsg] = useState("");
  const [title, setTitle] = useState("");
  const [destination, setDestination] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [activities, setActivities] = useState<Activity[]>([]);
  const [baseLocation, setBaseLocation] = useState("");
  const [baseLat, setBaseLat] = useState<number | null>(null);
  const [baseLng, setBaseLng] = useState<number | null>(null);
  const [lodging, setLodging] = useState<LodgingMap>({});
  const [persons, setPersons] = useState(1);
  // free-text mirror of `persons` so the field can be cleared while typing
  const [personsInput, setPersonsInput] = useState("1");
  const [currency, setCurrency] = useState("kr");
  const [tripStatus, setTripStatus] = useState<TripStatus>("planned");
  const [editing, setEditing] = useState<Editing>(null);

  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");
  const [dragId, setDragId] = useState<string | null>(null);
  const [dropTarget, setDropTarget] = useState<number | "pool" | null>(null);
  const [showSettings, setShowSettings] = useState(false);
  const [view, setView] = useState<"calendar" | "map" | "booking" | "budget" | "flights">(() => {
    const v = localStorage.getItem("planner-view");
    return v === "map" || v === "booking" || v === "budget" || v === "flights" ? v : "calendar"; // "agenda" (removed) → calendar
  });
  const changeView = (v: "calendar" | "map" | "booking" | "budget" | "flights") => {
    setView(v);
    localStorage.setItem("planner-view", v);
  };
  const history = useHistory();

  useEffect(() => {
    db.collection("tripPlans")
      .doc(planId)
      .get()
      .then((snap) => {
        const data = snap.data() as any;
        const shared: string[] = Array.isArray(data?.sharedWith) ? data.sharedWith : [];
        const myEmail = normEmail(email);
        if (!data || (data.uid !== uid && !shared.includes(myEmail))) {
          setStatus("notfound");
          return;
        }
        setOwnerUid(data.uid ?? "");
        setSharedWith(shared);
        const start = data.startDate ?? "";
        // legacy plans stored only startDate + days[]; derive an end date
        const acts = readActivities(data);
        let end = data.endDate ?? "";
        if (!end && start && Array.isArray(data.days) && data.days.length > 0) {
          end = addDaysISO(start, data.days.length - 1);
        }
        setTitle(data.title ?? "");
        setDestination(data.destination ?? "");
        setImageUrl(data.imageUrl ?? "");
        setStartDate(start);
        setEndDate(end);
        setActivities(acts);
        setBaseLocation(data.baseLocation ?? "");
        setBaseLat(typeof data.baseLat === "number" ? data.baseLat : null);
        setBaseLng(typeof data.baseLng === "number" ? data.baseLng : null);
        setLodging(data.lodging && typeof data.lodging === "object" ? data.lodging : {});
        const p = typeof data.persons === "number" && data.persons > 0 ? data.persons : 1;
        setPersons(p);
        setPersonsInput(String(p));
        setCurrency(typeof data.currency === "string" && data.currency.trim() ? data.currency : "kr");
        setTripStatus(tripStatusOf(data.status));
        setSaved(true); // freshly loaded = in sync with the DB
        setStatus("ready");
      })
      .catch(() => setStatus("notfound"));
  }, [planId, uid, email]);

  const isOwner = ownerUid === uid;

  const dc = countDays(startDate, endDate);
  const datesInvalid = !!startDate && !!endDate && dc === 0;

  const markDirty = () => setSaved(false);

  // When the range shrinks, push now-out-of-range cards back to the idea pool.
  const reconcile = (start: string, end: string) => {
    const next = countDays(start, end);
    setActivities((prev) =>
      prev.map((a) => (a.day !== null && a.day >= next ? { ...a, day: null } : a))
    );
  };

  const onStart = (v: string) => { setStartDate(v); reconcile(v, endDate); markDirty(); };
  const onEnd = (v: string) => { setEndDate(v); reconcile(startDate, v); markDirty(); };

  const saveActivity = (activity: Activity) => {
    setActivities((prev) =>
      prev.some((a) => a.id === activity.id)
        ? prev.map((a) => (a.id === activity.id ? activity : a))
        : [...prev, activity]
    );
    setEditing(null);
    markDirty();
  };

  // Add several cards at once (multiDay add → one card per selected day).
  const saveActivities = (list: Activity[]) => {
    if (list.length) {
      setActivities((prev) => [...prev, ...list]);
      markDirty();
    }
    setEditing(null);
  };

  const setActivityDay = (id: string, day: number | null) => {
    setActivities((prev) => prev.map((a) => (a.id === id ? { ...a, day } : a)));
    markDirty();
  };

  const deleteActivity = (id: string) => {
    setActivities((prev) => prev.filter((a) => a.id !== id));
    markDirty();
  };

  // --- sharing (owner only) ---
  const addShare = () => {
    const e = normEmail(shareEmail);
    if (!isValidEmail(e)) { setError("Ogiltig e-postadress."); return; }
    if (e === normEmail(email)) { setError("Det är din egen adress."); return; }
    setError("");
    setShareEmail("");
    if (!sharedWith.includes(e)) { setSharedWith((prev) => [...prev, e]); markDirty(); }
  };
  const removeShare = (e: string) => {
    setSharedWith((prev) => prev.filter((x) => x !== e));
    markDirty();
  };

  // --- drag & drop: move a card to a day (number) or back to the pool (null) ---
  const onDragStart = (e: React.DragEvent, id: string) => {
    setDragId(id);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", id);
  };
  const onDragEnd = () => { setDragId(null); setDropTarget(null); };
  const allowDrop = (target: number | "pool") => (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (dropTarget !== target) setDropTarget(target);
  };
  const handleDrop = (target: number | null) => (e: React.DragEvent) => {
    e.preventDefault();
    const id = dragId || e.dataTransfer.getData("text/plain");
    if (id) setActivityDay(id, target);
    setDragId(null);
    setDropTarget(null);
  };

  const handleSave = async () => {
    setSaving(true);
    setError("");
    try {
      await db.collection("tripPlans").doc(planId).update({
        title: title.trim() || "Namnlös resa",
        destination: destination.trim(),
        imageUrl: imageUrl.trim(),
        startDate,
        endDate,
        activities,
        baseLocation,
        baseLat,
        baseLng,
        lodging,
        sharedWith,
        persons,
        currency,
        status: tripStatus,
        days: firebase.firestore.FieldValue.delete(), // drop legacy field
      });
      setSaved(true);
    } catch (e: any) {
      setError(e?.message || "Kunde inte spara.");
    }
    setSaving(false);
  };

  // Autosave: debounce a save ~1s after the last edit. Skip the first run
  // (triggered by the load setting state) so opening a plan doesn't re-write it.
  const loadedOnce = useRef(false);
  useEffect(() => {
    if (status !== "ready") return;
    if (!loadedOnce.current) { loadedOnce.current = true; return; }
    const t = window.setTimeout(() => { void handleSave(); }, 1000);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, destination, imageUrl, startDate, endDate, activities, baseLocation, baseLat, baseLng, lodging, sharedWith, persons, currency, tripStatus, status]);

  const handleDeletePlan = async () => {
    if (!window.confirm("Ta bort hela planen? Det går inte att ångra.")) return;
    try {
      await db.collection("tripPlans").doc(planId).delete();
      history.push(routeUrls.PLANNER);
    } catch (e: any) {
      setError(e?.message || "Kunde inte ta bort planen.");
    }
  };

  // Build the calendar grid: whole weeks (Mon–Sun) covering the trip range,
  // "zoomed in" so it starts the week of the start date and ends the week of
  // the end date. Each cell knows whether it's inside the trip and its day index.
  const WEEKDAYS = ["Mån", "Tis", "Ons", "Tor", "Fre", "Lör", "Sön"];
  const buildCalendar = () => {
    const s = parseISO(startDate);
    const e = parseISO(endDate);
    if (!s || !e || dc === 0) return [];
    const mondayIdx = (d: Date) => (d.getDay() + 6) % 7; // 0 = Monday
    const gridStart = new Date(s);
    gridStart.setDate(s.getDate() - mondayIdx(s));
    const gridEnd = new Date(e);
    gridEnd.setDate(e.getDate() + (6 - mondayIdx(e)));

    type Cell = { date: Date; inRange: boolean; dayIndex: number };
    const weeks: Cell[][] = [];
    const cur = new Date(gridStart);
    while (cur <= gridEnd) {
      const week: Cell[] = [];
      for (let i = 0; i < 7; i++) {
        const inRange = cur >= s && cur <= e;
        const dayIndex = Math.round((cur.getTime() - s.getTime()) / 86400000);
        week.push({ date: new Date(cur), inRange, dayIndex });
        cur.setDate(cur.getDate() + 1);
      }
      weeks.push(week);
    }
    return weeks;
  };

  // Date for a 0-based day index (for the agenda day headers).
  const dayDate = (i: number): Date | null => {
    const s = parseISO(startDate);
    if (!s) return null;
    const d = new Date(s);
    d.setDate(s.getDate() + i);
    return d;
  };

  // Where you sleep on a given day: per-day override if it has coords, else the
  // trip's base accommodation.
  const effectiveBase = (day: number): LodgingPoint => {
    const o = lodging[String(day)];
    if (o && o.lat !== null && o.lng !== null) return o;
    return { location: baseLocation, lat: baseLat, lng: baseLng };
  };

  const setDayLodging = (day: number, point: LodgingPoint) => {
    setLodging((prev) => ({ ...prev, [String(day)]: point }));
    markDirty();
  };
  const clearDayLodging = (day: number) => {
    setLodging((prev) => {
      const next = { ...prev };
      delete next[String(day)];
      return next;
    });
    markDirty();
  };

  // Auto-fill driving Restid from each day's base to each scheduled activity's
  // start. Only fills blank or previously-auto values (manual edits stick).
  // Cached per from→to pair so we don't re-hit OSRM; converges then idles.
  const routeCache = useRef<Map<string, number | null>>(new Map());
  useEffect(() => {
    if (dc === 0) return;
    let cancelled = false;
    (async () => {
      for (const a of activities) {
        if (a.day === null || a.day >= dc) continue;
        if (a.type === "flight" || a.type === "train" || a.type === "boat") continue; // not a car drive
        if (a.travelTime && !a.travelAuto) continue; // manual override wins

        // For a drive (Resa) the time IS the journey start→end; for everything
        // else it's the drive from the hotel to where the activity is.
        const isTravel = a.type === "travel";
        const toLat = isTravel ? a.endLat : a.lat;
        const toLng = isTravel ? a.endLng : a.lng;
        if (toLat == null || toLng == null) continue;

        // Origin: custom override → the journey's own start (drives) → the hotel.
        let fromLat: number | null;
        let fromLng: number | null;
        if (a.travelFromOther && a.originLat != null && a.originLng != null) {
          fromLat = a.originLat; fromLng = a.originLng;
        } else if (isTravel && a.lat != null && a.lng != null) {
          fromLat = a.lat; fromLng = a.lng;
        } else {
          const base = effectiveBase(a.day);
          fromLat = base.lat; fromLng = base.lng;
        }
        if (fromLat == null || fromLng == null) continue;

        const key = `${fromLat},${fromLng}>${toLat},${toLng}`;
        let mins = routeCache.current.get(key);
        if (mins === undefined) {
          mins = await driveMinutes(fromLat, fromLng, toLat, toLng);
          routeCache.current.set(key, mins);
        }
        if (cancelled || mins === null) continue;
        const label = formatDriveTime(mins);
        if (a.travelTime !== label) {
          setActivities((prev) =>
            prev.map((x) => (x.id === a.id ? { ...x, travelTime: label, travelAuto: true } : x))
          );
          setSaved(false);
        }
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activities, lodging, baseLat, baseLng, dc]);

  // "Ta bort" only deletes when the card is in the idea pool; a scheduled card
  // is moved back to the pool instead (so you don't lose it from the plan).
  // --- flights (managed from settings; auto-placed on their calendar day) ---
  const dateToDayIndex = (iso: string): number => {
    const s = parseISO(startDate);
    const d = parseISO(iso);
    if (!s || !d) return -1;
    return Math.round((d.getTime() - s.getTime()) / 86400000);
  };
  const applyFlightInfo = (a: Activity, f: FlightLookupResult): Activity => ({
    ...a,
    title: [f.airline, f.number].filter(Boolean).join(" ") || a.title || `Flyg ${a.flightNumber}`,
    location: f.departure.name || a.location,
    lat: f.departure.lat,
    lng: f.departure.lng,
    endLocation: f.arrival.name || a.endLocation,
    endLat: f.arrival.lat,
    endLng: f.arrival.lng,
    time: f.departure.timeLocal || a.time,
    arrTime: f.arrival.timeLocal || a.arrTime,
  });
  const addFlight = async () => {
    const number = flightNum.trim();
    if (!number) return;
    setFlightBusy(true);
    setFlightMsg("");
    let results: FlightLookupResult[] = [];
    let errored = false;
    try { results = await lookupFlightAny(number); }
    catch (e: any) { errored = true; setFlightMsg(e?.message || "Kunde inte hämta flyg."); }
    // pick the occurrence whose date falls within the trip (ISO strings compare ok)
    const inRange = results.find((r) => r.date && r.date >= startDate && r.date <= endDate);
    let act: Activity = { ...emptyActivity(), type: "flight", flightNumber: number, title: `Flyg ${number}`, day: null };
    if (inRange) {
      act = applyFlightInfo(act, inRange);
      const idx = dateToDayIndex(inRange.date as string);
      act.day = idx >= 0 && idx < dc ? idx : null;
    } else if (results.length > 0) {
      setFlightMsg("Flyget hittades men inte inom resans datum — lade till utan dag.");
    } else if (!errored) {
      setFlightMsg("Hittade inget flyg ännu — fylls i närmare resan (tryck Hämta).");
    }
    setActivities((prev) => [...prev, act]);
    markDirty();
    setFlightNum("");
    setFlightBusy(false);
  };
  const refreshFlight = async (a: Activity) => {
    if (!a.flightNumber) return;
    setFlightBusy(true);
    setFlightMsg("");
    try {
      // If it has a day, look up that exact date; otherwise find the in-range one.
      if (a.day != null) {
        const f = await lookupFlight(a.flightNumber, addDaysISO(startDate, a.day));
        if (f) { setActivities((prev) => prev.map((x) => (x.id === a.id ? applyFlightInfo(x, f) : x))); markDirty(); }
        else setFlightMsg("Hittade ingen flyginfo ännu för det datumet.");
      } else {
        const results = await lookupFlightAny(a.flightNumber);
        const inRange = results.find((r) => r.date && r.date >= startDate && r.date <= endDate);
        if (inRange) {
          const idx = dateToDayIndex(inRange.date as string);
          setActivities((prev) =>
            prev.map((x) => (x.id === a.id ? { ...applyFlightInfo(x, inRange), day: idx >= 0 && idx < dc ? idx : x.day } : x))
          );
          markDirty();
        } else setFlightMsg("Hittade inget flyg inom resans datum ännu.");
      }
    } catch (e: any) { setFlightMsg(e?.message || "Kunde inte hämta flyg."); }
    setFlightBusy(false);
  };

  const toggleBooked = (id: string) => {
    setActivities((prev) => prev.map((a) => (a.id === id ? { ...a, booked: !a.booked } : a)));
    markDirty();
  };

  const removeCard = (a: Activity) => {
    const inPool = a.day === null || a.day >= dc;
    if (inPool) deleteActivity(a.id);
    else setActivityDay(a.id, null);
  };

  // Cards show only the title (+ a small time hint); click to see all details.
  const renderCard = (a: Activity, compact = false, deletable = false) => (
    <div
      key={a.id}
      className={`planner__card${compact ? " planner__card--compact" : ""}${dragId === a.id ? " planner__card--dragging" : ""}`}
      style={{ borderLeftColor: ACTIVITY_TYPES[a.type].color }}
      draggable
      onDragStart={(e) => onDragStart(e, a.id)}
      onDragEnd={onDragEnd}
      onClick={() => setEditing({ mode: "view", id: a.id })}
      title="Visa detaljer"
    >
      {deletable && (
        <button
          className="planner__card-del"
          title="Ta bort idé"
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => { e.stopPropagation(); deleteActivity(a.id); }}
        >
          ×
        </button>
      )}
      <div className="planner__card-head">
        <span className="planner__card-icon">{ACTIVITY_TYPES[a.type].icon}</span>
        <span className="planner__card-name">{a.title}</span>
        {activitySpan(a) > 1 && <span className="planner__card-time">📆 {activitySpan(a)} dagar</span>}
        {a.time && <span className="planner__card-time">{a.time}</span>}
      </div>
      {!compact && a.location && (
        <span className="planner__card-loc">
          📍 {a.location}
          {hasRoute(a.type) && a.endLocation ? ` → ${a.endLocation}` : ""}
        </span>
      )}
      {(effectiveBooking(a) !== "none" || a.travelTime || a.link || a.flightNumber) && (
        <div className="planner__card-badges">
          {a.flightNumber && <span className="planner__badge">✈️ {a.flightNumber}</span>}
          {effectiveBooking(a) !== "none" && (
            <span
              className="planner__badge"
              style={{ color: BOOKING_STATUS[effectiveBooking(a)].color }}
              title={BOOKING_STATUS[effectiveBooking(a)].label}
            >
              {BOOKING_STATUS[effectiveBooking(a)].badge} {BOOKING_STATUS[effectiveBooking(a)].label}
            </span>
          )}
          {a.travelTime && <span className="planner__badge">🚗 {a.travelTime}</span>}
          {a.link && <span className="planner__badge" title={linkKind(a.link).label}>{linkKind(a.link).icon} {linkKind(a.link).label}</span>}
        </div>
      )}
    </div>
  );

  const modalActivity =
    editing && (editing.mode === "edit" || editing.mode === "view")
      ? activities.find((a) => a.id === editing.id)
      : undefined;

  if (status === "loading") return <div className="planner"><p className="planner__empty">Laddar…</p></div>;
  if (status === "notfound")
    return (
      <div className="planner">
        <p className="planner__empty">Planen hittades inte.</p>
        <div className="planner__center">
          <NavLink to={routeUrls.PLANNER} className="planner__back planner__back--static">← Till planeraren</NavLink>
        </div>
      </div>
    );

  const pool = activities.filter((a) => a.day === null || a.day >= dc);

  return (
    <div className="planner">
      <div className="planner__header">
        <NavLink to={routeUrls.PLANNER} className="planner__back">← Planer</NavLink>
        <h1 className="planner__title">Planera resa</h1>
        <span className="planner__savestate" aria-live="polite">
          {saving ? "Sparar…" : saved ? "Sparad ✓" : "Ändrat…"}
        </span>
      </div>

      {dc > 0 && (
        <div className="planner__settings-bar">
          <button
            className={`planner__settings-toggle${showSettings ? " planner__settings-toggle--open" : ""}`}
            onClick={() => setShowSettings((s) => !s)}
          >
            ⚙️ {showSettings ? "Dölj inställningar" : "Reseinställningar"}
          </button>
          {!showSettings && (
            <span className="planner__settings-name">
              {title.trim() || "Namnlös resa"}
              {destination ? ` · ${destination}` : ""}
            </span>
          )}
        </div>
      )}

      {(showSettings || dc === 0) && (
      <div className="planner__meta">
        <input
          className="planner__input planner__input--lg"
          value={title}
          onChange={(e) => { setTitle(e.target.value); markDirty(); }}
          placeholder="Resans namn"
        />
        <div className="planner__status-pick">
          {TRIP_STATUS_ORDER.map((s) => (
            <button
              key={s}
              type="button"
              className={`planner__status-btn${tripStatus === s ? " planner__status-btn--on" : ""}`}
              style={tripStatus === s ? { background: TRIP_STATUS[s].color, borderColor: TRIP_STATUS[s].color } : undefined}
              onClick={() => { setTripStatus(s); markDirty(); }}
            >
              {TRIP_STATUS[s].icon} {TRIP_STATUS[s].label}
            </button>
          ))}
        </div>
        <div className="planner__meta-row">
          <input
            className="planner__input"
            value={destination}
            onChange={(e) => { setDestination(e.target.value); markDirty(); }}
            placeholder="Destination"
          />
          <label className="planner__field">
            <span className="planner__field-label">Startdatum</span>
            <input className="planner__input" type="date" value={startDate} onChange={(e) => onStart(e.target.value)} />
          </label>
          <label className="planner__field">
            <span className="planner__field-label">Slutdatum</span>
            <input
              className="planner__input"
              type="date"
              value={endDate}
              min={startDate || undefined}
              onChange={(e) => onEnd(e.target.value)}
            />
          </label>
        </div>
        <label className="planner__field">
          <span className="planner__field-label">🖼️ Bild (länk)</span>
          <input
            className="planner__input"
            type="url"
            value={imageUrl}
            onChange={(e) => { setImageUrl(e.target.value); markDirty(); }}
            placeholder="https://…/bild.jpg"
          />
          {imageUrl.trim() && (
            <img
              className="planner__meta-img"
              src={imageUrl}
              alt=""
              onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = "none"; }}
              onLoad={(e) => { (e.currentTarget as HTMLImageElement).style.display = "block"; }}
            />
          )}
        </label>

        <label className="planner__field">
          <span className="planner__field-label">🏨 Boende (bas) — för att räkna ut restid</span>
          <LocationAutocomplete
            value={baseLocation}
            hasCoords={baseLat !== null}
            placeholder="Var bor ni? (autocomplete)"
            onText={(t) => { setBaseLocation(t); setBaseLat(null); setBaseLng(null); markDirty(); }}
            onPick={(r) => { setBaseLocation(r.label); setBaseLat(r.lat); setBaseLng(r.lng); markDirty(); }}
          />
        </label>

        <div className="planner__meta-row">
          <label className="planner__field">
            <span className="planner__field-label">👥 Antal personer (delar budgeten)</span>
            <input
              className="planner__input"
              type="number"
              min={1}
              value={personsInput}
              onChange={(e) => {
                setPersonsInput(e.target.value);
                const n = parseInt(e.target.value, 10);
                if (n > 0) { setPersons(n); markDirty(); }
              }}
              onBlur={() => setPersonsInput(String(persons))}
            />
          </label>
          <label className="planner__field">
            <span className="planner__field-label">💱 Valuta</span>
            <input
              className="planner__input"
              value={currency}
              onChange={(e) => { setCurrency(e.target.value); markDirty(); }}
              placeholder="kr"
            />
          </label>
        </div>

        {dc > 0 && (
          <div className="planner__field">
            <span className="planner__field-label">✈️ Flyg — anges med flightnr, läggs på rätt dag</span>
            <div className="planner__flight-add">
              <input
                className="planner__input"
                value={flightNum}
                onChange={(e) => { setFlightNum(e.target.value); setFlightMsg(""); }}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addFlight(); } }}
                placeholder="Flightnr (t.ex. D84365)"
              />
              <button
                type="button"
                className="planner__btn"
                disabled={flightBusy || !flightNum.trim()}
                onClick={addFlight}
              >
                {flightBusy ? "…" : "Lägg till"}
              </button>
            </div>
            {flightMsg && <span className="planner__at-hint">{flightMsg}</span>}
            {(() => {
              const flights = activities
                .filter((a) => a.type === "flight")
                .sort((a, b) => (a.day ?? 99) - (b.day ?? 99));
              if (flights.length === 0) return null;
              return (
                <ul className="planner__share-list">
                  {flights.map((a) => (
                    <li key={a.id} className="planner__share-item">
                      <span>
                        ✈️ {a.flightNumber || a.title}
                        {a.location ? ` · ${a.location.split("(")[0].trim()} → ${(a.endLocation || "").split("(")[0].trim()}` : ""}
                      </span>
                      <span style={{ display: "flex", gap: "0.6rem", alignItems: "center", flexShrink: 0 }}>
                        <select
                          className="planner__day-select"
                          title="Vilken dag?"
                          value={a.day ?? ""}
                          onChange={(e) => setActivityDay(a.id, e.target.value === "" ? null : Number(e.target.value))}
                        >
                          <option value="">ingen dag</option>
                          {Array.from({ length: dc }, (_, i) => (
                            <option key={i} value={i}>Dag {i + 1}</option>
                          ))}
                        </select>
                        <button className="planner__link-btn" onClick={() => refreshFlight(a)}>Hämta</button>
                        <button className="planner__link-btn planner__link-btn--danger" onClick={() => deleteActivity(a.id)}>Ta bort</button>
                      </span>
                    </li>
                  ))}
                </ul>
              );
            })()}
          </div>
        )}

        {isOwner ? (
          <div className="planner__field">
            <span className="planner__field-label">🤝 Dela med (e-post)</span>
            <div className="planner__share-add">
              <input
                className="planner__input"
                type="email"
                value={shareEmail}
                onChange={(e) => setShareEmail(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addShare(); } }}
                placeholder="namn@example.com"
              />
              <button type="button" className="planner__btn" onClick={addShare}>Lägg till</button>
            </div>
            {sharedWith.length > 0 && (
              <ul className="planner__share-list">
                {sharedWith.map((e) => (
                  <li key={e} className="planner__share-item">
                    <span>{e}</span>
                    <button
                      type="button"
                      className="planner__link-btn planner__link-btn--danger"
                      onClick={() => removeShare(e)}
                    >Ta bort</button>
                  </li>
                ))}
              </ul>
            )}
            <span className="planner__day-hint">
              Personen måste ha ett konto på sajten. Ändringar sparas automatiskt.
            </span>
          </div>
        ) : (
          <p className="planner__day-hint">🤝 Delad plan – du redigerar tillsammans med ägaren.</p>
        )}

        {datesInvalid && <p className="planner__error">Slutdatum måste vara samma dag eller efter startdatum.</p>}
        {error && <p className="planner__error">{error}</p>}
        {isOwner && (
          <button className="planner__delete-plan" onClick={handleDeletePlan}>
            Ta bort plan
          </button>
        )}
      </div>
      )}

      {dc > 0 && (() => {
        const planned = activities.filter((a) => a.day !== null && a.day < dc);
        const needsBooking = planned.filter((a) => effectiveBooking(a) === "required");
        const bookedCount = needsBooking.filter((a) => a.booked).length;
        const allBooked = needsBooking.length > 0 && bookedCount === needsBooking.length;
        return (
          <div className="planner__summary">
            <span className="planner__summary-stat"><b>{dc}</b> dagar</span>
            <span className="planner__summary-stat"><b>{planned.length}</b> inplanerade</span>
            {needsBooking.length > 0 && (
              <span className={`planner__summary-stat${allBooked ? "" : " planner__summary-stat--warn"}`}>
                🎫 <b>{bookedCount}/{needsBooking.length}</b> bokade
              </span>
            )}
            <span className="planner__summary-stat"><b>{pool.length}</b> idéer</span>
          </div>
        );
      })()}

      {/* Idea pool: add anything you could do, then drag it onto a day */}
      <div className="planner__pool">
        <div className="planner__pool-head">
          <h3 className="planner__pool-title">💡 Idéer</h3>
          <span className="planner__day-hint">Skapa allt ni vill göra här, lägg sedan i kalendern genom att dra eller trycka på + i en dag.</span>
        </div>
        <div
          className={`planner__pool-cards${dropTarget === "pool" ? " planner__pool-cards--drop" : ""}`}
          onDragOver={allowDrop("pool")}
          onDrop={handleDrop(null)}
          onDragLeave={() => setDropTarget((t) => (t === "pool" ? null : t))}
        >
          {pool.map((a) => renderCard(a, true, true))}
          <button className="planner__add" onClick={() => setEditing({ mode: "add" })}>
            + Lägg till idé
          </button>
        </div>
      </div>

      {datesInvalid ? null : dc === 0 ? (
        <p className="planner__empty">Ange start- och slutdatum för att lägga upp dagarna.</p>
      ) : (
        <>
        <div className="planner__viewtoggle">
          <button
            className={`planner__viewtoggle-btn${view === "calendar" ? " planner__viewtoggle-btn--active" : ""}`}
            onClick={() => changeView("calendar")}
          >📅 Kalender</button>
          <button
            className={`planner__viewtoggle-btn${view === "map" ? " planner__viewtoggle-btn--active" : ""}`}
            onClick={() => changeView("map")}
          >🗺️ Karta</button>
          <button
            className={`planner__viewtoggle-btn${view === "booking" ? " planner__viewtoggle-btn--active" : ""}`}
            onClick={() => changeView("booking")}
          >🎫 Boka</button>
          <button
            className={`planner__viewtoggle-btn${view === "flights" ? " planner__viewtoggle-btn--active" : ""}`}
            onClick={() => changeView("flights")}
          >✈️ Flyg</button>
          <button
            className={`planner__viewtoggle-btn${view === "budget" ? " planner__viewtoggle-btn--active" : ""}`}
            onClick={() => changeView("budget")}
          >💰 Budget</button>
        </div>

        {view === "calendar" ? (
        <div className="planner__cal">
          <div className="planner__cal-weekdays">
            {WEEKDAYS.map((w) => (
              <span key={w} className="planner__cal-weekday">{w}</span>
            ))}
          </div>
          {buildCalendar().map((week, wi) => (
            <div key={wi} className="planner__cal-week">
              {week.map((cell) =>
                cell.inRange ? (
                  <div
                    key={cell.date.toISOString()}
                    className={`planner__cal-cell planner__cal-cell--clickable${dropTarget === cell.dayIndex ? " planner__cal-cell--drop" : ""}`}
                    onClick={() => setEditing({ mode: "day", day: cell.dayIndex })}
                    title="Visa dagen"
                    onDragOver={allowDrop(cell.dayIndex)}
                    onDrop={handleDrop(cell.dayIndex)}
                    onDragLeave={() => setDropTarget((t) => (t === cell.dayIndex ? null : t))}
                  >
                    <div className="planner__cal-date">
                      <span className="planner__cal-weekday-inline">
                        {cell.date.toLocaleDateString("sv-SE", { weekday: "short" })}
                      </span>
                      <span className="planner__cal-daynum">{cell.date.getDate()}</span>
                      <span className="planner__cal-month">
                        {cell.date.toLocaleDateString("sv-SE", { month: "short" })}
                      </span>
                    </div>
                    {activities
                      .filter((a) => coversDay(a, cell.dayIndex))
                      .sort(byTime)
                      .map((a) => {
                        const isStart = a.day === cell.dayIndex;
                        const span = activitySpan(a);
                        return (
                          <span
                            key={a.id}
                            className={`planner__cal-chip${dragId === a.id ? " planner__cal-chip--dragging" : ""}${isStart ? "" : " planner__cal-chip--cont"}`}
                            style={{
                              background: `${ACTIVITY_TYPES[a.type].color}22`,
                              borderLeftColor: ACTIVITY_TYPES[a.type].color,
                            }}
                            draggable={isStart}
                            onDragStart={(e) => { e.stopPropagation(); onDragStart(e, a.id); }}
                            onDragEnd={onDragEnd}
                          >
                            {isStart
                              ? `${ACTIVITY_TYPES[a.type].icon} ${a.title}${span > 1 ? ` (1/${span})` : ""}`
                              : `↳ ${a.title} (${cell.dayIndex - (a.day as number) + 1}/${span})`}
                          </span>
                        );
                      })}
                  </div>
                ) : (
                  <div key={cell.date.toISOString()} className="planner__cal-cell planner__cal-cell--out">
                    <span className="planner__cal-daynum">{cell.date.getDate()}</span>
                  </div>
                )
              )}
            </div>
          ))}
        </div>
        ) : view === "map" ? (
          <div className="planner__map-section">
            {/* only planned (scheduled to a valid day) activities are mapped */}
            <TripMap
              activities={activities.filter((a) => a.day !== null && a.day < dc)}
              startISO={startDate}
              dayCount={dc}
              lodgings={(() => {
                const seen = new Set<string>();
                const out: { label: string; lat: number; lng: number }[] = [];
                const add = (p: LodgingPoint) => {
                  if (p.lat === null || p.lng === null) return;
                  const k = `${p.lat},${p.lng}`;
                  if (seen.has(k)) return;
                  seen.add(k);
                  out.push({ label: p.location, lat: p.lat, lng: p.lng });
                };
                add({ location: baseLocation, lat: baseLat, lng: baseLng });
                Object.values(lodging).forEach(add);
                return out;
              })()}
              bases={(() => {
                const out: { day: number; lat: number; lng: number }[] = [];
                for (let i = 0; i < dc; i++) {
                  const b = effectiveBase(i);
                  if (b.lat !== null && b.lng !== null) out.push({ day: i, lat: b.lat, lng: b.lng });
                }
                return out;
              })()}
            />
          </div>
        ) : view === "booking" ? (
          <div className="planner__booking">
            {(() => {
              const toBook = activities
                .filter((a) => effectiveBooking(a) !== "none")
                .sort((a, b) => {
                  const rank = (x: Activity) => (effectiveBooking(x) === "required" ? 0 : 1);
                  if (rank(a) !== rank(b)) return rank(a) - rank(b);
                  const da = a.day === null || a.day >= dc ? 999 : a.day;
                  const dbb = b.day === null || b.day >= dc ? 999 : b.day;
                  return da - dbb;
                });
              if (toBook.length === 0)
                return <p className="planner__empty">Inga aktiviteter behöver bokas. 🎉</p>;
              return toBook.map((a) => {
                const inPool = a.day === null || a.day >= dc;
                const d = inPool ? null : dayDate(a.day as number);
                return (
                  <div key={a.id} className={`planner__book-row${a.booked ? " planner__book-row--done" : ""}`}>
                    <label className="planner__book-check">
                      <input type="checkbox" checked={!!a.booked} onChange={() => toggleBooked(a.id)} />
                    </label>
                    <button
                      className="planner__book-main"
                      style={{ borderLeftColor: ACTIVITY_TYPES[a.type].color }}
                      onClick={() => setEditing({ mode: "view", id: a.id })}
                    >
                      <span className="planner__book-head">
                        <span className="planner__card-icon">{ACTIVITY_TYPES[a.type].icon}</span>
                        <span className="planner__book-title">{a.title}</span>
                      </span>
                      <span className="planner__book-meta">
                        <span style={{ color: BOOKING_STATUS[effectiveBooking(a)].color }}>
                          {BOOKING_STATUS[effectiveBooking(a)].badge} {BOOKING_STATUS[effectiveBooking(a)].label}
                        </span>
                        {" · "}
                        {inPool
                          ? "Idé"
                          : `Dag ${(a.day as number) + 1}${d ? " · " + d.toLocaleDateString("sv-SE", { day: "numeric", month: "short" }) : ""}`}
                      </span>
                      {a.bookingInfo && <span className="planner__book-info">🎫 {a.bookingInfo}</span>}
                    </button>
                    {a.link && (
                      <a
                        className="planner__book-link"
                        href={a.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        title={linkKind(a.link).label}
                      >
                        {linkKind(a.link).icon}
                      </a>
                    )}
                  </div>
                );
              });
            })()}
          </div>
        ) : view === "budget" ? (
          <BudgetPanel
            activities={activities.filter((a) => a.day !== null && a.day < dc)}
            persons={persons}
            currency={currency}
          />
        ) : (
          <div className="planner__flights">
            <div className="planner__flight-add">
              <input
                className="planner__input"
                value={flightNum}
                onChange={(e) => { setFlightNum(e.target.value); setFlightMsg(""); }}
                placeholder="✈️ Flightnummer (t.ex. D84365)"
                onKeyDown={(e) => { if (e.key === "Enter") addFlight(); }}
              />
              <button className="planner__btn" disabled={flightBusy || !flightNum.trim()} onClick={addFlight}>
                {flightBusy ? "…" : "Lägg till"}
              </button>
            </div>
            {flightMsg && <span className="planner__at-hint">{flightMsg}</span>}
            {(() => {
              const flights = activities
                .filter((a) => a.type === "flight")
                .sort((a, b) => {
                  const da = a.day === null || a.day >= dc ? 999 : a.day;
                  const db = b.day === null || b.day >= dc ? 999 : b.day;
                  if (da !== db) return da - db;
                  return byTime(a, b);
                });
              if (flights.length === 0)
                return <p className="planner__empty">Inga flyg ännu. Lägg till med flightnummer ovan.</p>;
              return flights.map((a) => {
                const inPool = a.day === null || a.day >= dc;
                const d = inPool ? null : dayDate(a.day as number);
                return (
                  <div key={a.id} className="planner__flight-row">
                    <button className="planner__flight-main" onClick={() => setEditing({ mode: "view", id: a.id })}>
                      <span className="planner__flight-top">
                        <span className="planner__flight-no">✈️ {a.title || `Flyg ${a.flightNumber}`}</span>
                        <span className="planner__flight-day">
                          {inPool ? "Ingen dag" : `Dag ${(a.day as number) + 1}${d ? " · " + d.toLocaleDateString("sv-SE", { day: "numeric", month: "short" }) : ""}`}
                        </span>
                      </span>
                      <span className="planner__flight-legs">
                        <span className="planner__flight-leg">
                          <span className="planner__flight-time">{a.time || "--:--"}</span>
                          <span className="planner__flight-place">{a.location || "Avreseort"}</span>
                        </span>
                        <span className="planner__flight-arrow">→</span>
                        <span className="planner__flight-leg">
                          <span className="planner__flight-time">{a.arrTime || "--:--"}</span>
                          <span className="planner__flight-place">{a.endLocation || "Ankomstort"}</span>
                        </span>
                      </span>
                    </button>
                    <button className="planner__flight-refresh" title="Uppdatera tider" disabled={flightBusy || !a.flightNumber} onClick={() => refreshFlight(a)}>↻</button>
                  </div>
                );
              });
            })()}
          </div>
        )}
        </>
      )}

      {editing && (editing.mode === "add" || editing.mode === "new" || editing.mode === "pick" || editing.mode === "lodging" || editing.mode === "day" || modalActivity) && (
        <div className="planner__overlay" onClick={() => setEditing(null)}>
          <div className="planner__modal" onClick={(e) => e.stopPropagation()}>
            {editing.mode === "day" ? (
              (() => {
                const day = editing.day;
                const acts = activities.filter((a) => coversDay(a, day)).sort(byTime);
                const d = dayDate(day);
                const eb = effectiveBase(day);
                return (
                  <div className="planner__picker">
                    <h3 className="planner__picker-title">
                      Dag {day + 1}
                      {d && ` · ${d.toLocaleDateString("sv-SE", { weekday: "long", day: "numeric", month: "short" })}`}
                    </h3>
                    <button
                      className={`planner__agenda-stay${eb.lat !== null ? " planner__agenda-stay--set" : ""}`}
                      onClick={() => setEditing({ mode: "lodging", day })}
                    >
                      🏨 {eb.lat !== null ? eb.location : "Sätt boende"}
                    </button>
                    {acts.length === 0 ? (
                      <p className="planner__day-hint">Inget inplanerat denna dag.</p>
                    ) : (
                      acts.map((a) => (
                        <div key={a.id} className="planner__day-act">
                          {renderCard(a)}
                          <select
                            className="planner__day-select"
                            title="Flytta till annan dag"
                            value={a.day ?? ""}
                            onChange={(e) => setActivityDay(a.id, e.target.value === "" ? null : Number(e.target.value))}
                          >
                            {Array.from({ length: dc }, (_, i) => (
                              <option key={i} value={i}>Dag {i + 1}</option>
                            ))}
                            <option value="">→ Idéer</option>
                          </select>
                        </div>
                      ))
                    )}
                    <div className="planner__form-actions">
                      <button className="planner__btn planner__btn--ghost" onClick={() => setEditing({ mode: "pick", day })}>
                        + Lägg till
                      </button>
                      <button className="planner__btn" onClick={() => setEditing(null)}>Klar</button>
                    </div>
                  </div>
                );
              })()
            ) : editing.mode === "lodging" ? (
              <LodgingEditor
                day={editing.day}
                initial={lodging[String(editing.day)]}
                baseLabel={baseLocation}
                onSave={(p) => { setDayLodging(editing.day, p); setEditing(null); }}
                onUseBase={() => { clearDayLodging(editing.day); setEditing(null); }}
                onCancel={() => setEditing(null)}
              />
            ) : editing.mode === "pick" ? (
              <div className="planner__picker">
                <h3 className="planner__picker-title">Lägg till på Dag {editing.day + 1}</h3>
                <button
                  className="planner__picker-new"
                  onClick={() => setEditing({ mode: "new", day: editing.day })}
                >
                  ➕ Skapa ny aktivitet
                </button>
                {pool.length > 0 && <p className="planner__picker-sub">…eller välj från Idéer</p>}
                {pool.length === 0 ? (
                  <p className="planner__day-hint">Inga sparade idéer ännu.</p>
                ) : (
                  pool.map((a) => (
                    <button
                      key={a.id}
                      className="planner__picker-item"
                      style={{ borderLeftColor: ACTIVITY_TYPES[a.type].color }}
                      onClick={() => setActivityDay(a.id, editing.day)}
                    >
                      <span className="planner__card-icon">{ACTIVITY_TYPES[a.type].icon}</span>
                      <span className="planner__card-name">{a.title}</span>
                      {a.time && <span className="planner__card-time">{a.time}</span>}
                    </button>
                  ))
                )}
                <div className="planner__form-actions">
                  <button className="planner__btn" onClick={() => setEditing(null)}>Klar</button>
                </div>
              </div>
            ) : editing.mode === "view" && modalActivity ? (
              (() => {
                const a = modalActivity;
                const isRouted = hasRoute(a.type);
                const inPool = a.day === null || a.day >= dc;
                return (
                  <div className="planner__detail">
                    <div className="planner__detail-head" style={{ borderLeftColor: ACTIVITY_TYPES[a.type].color }}>
                      <span className="planner__detail-icon">{ACTIVITY_TYPES[a.type].icon}</span>
                      <h3 className="planner__detail-title">{a.title}</h3>
                    </div>
                    <p className="planner__detail-meta">
                      {ACTIVITY_TYPES[a.type].label}
                      {a.time && <> · {a.time}</>}
                    </p>
                    {a.location && (
                      <p className="planner__detail-row">📍 {isRouted ? "Start: " : ""}{a.location}</p>
                    )}
                    {isRouted && a.endLocation && (
                      <p className="planner__detail-row">🏁 Mål: {a.endLocation}</p>
                    )}
                    {a.flightNumber && <p className="planner__detail-row">✈️ Flyg: {a.flightNumber}</p>}
                    {a.type === "flight" && (a.time || a.arrTime) && (
                      <p className="planner__detail-row">🛫 Avresa {a.time || "--:--"} · 🛬 Ankomst {a.arrTime || "--:--"}</p>
                    )}
                    {a.travelTime && <p className="planner__detail-row">🚗 Restid: {a.travelTime}</p>}
                    {a.travelFromOther && a.originLocation && (
                      <p className="planner__detail-row">🚩 Från: {a.originLocation}</p>
                    )}
                    {a.parking && <p className="planner__detail-row">🅿️ Parkering: {a.parking}</p>}
                    {(a.price ?? 0) > 0 && (
                      <p className="planner__detail-row">💰 Pris: {Math.round(a.price as number).toLocaleString("sv-SE")} {currency}</p>
                    )}
                    {effectiveBooking(a) !== "none" && (
                      <p className="planner__detail-row" style={{ color: BOOKING_STATUS[effectiveBooking(a)].color }}>
                        {BOOKING_STATUS[effectiveBooking(a)].badge} {BOOKING_STATUS[effectiveBooking(a)].label}
                      </p>
                    )}
                    {a.bookingInfo && (
                      <p className="planner__detail-row">🎫 {a.bookingInfo}</p>
                    )}
                    {a.link && (() => {
                      const k = linkKind(a.link);
                      return (
                        <p className="planner__detail-row">
                          {k.icon}{" "}
                          <a className="planner__detail-link" href={a.link} target="_blank" rel="noopener noreferrer">
                            {k.label === "Länk" ? a.link : `Öppna i ${k.label}`}
                          </a>
                        </p>
                      );
                    })()}
                    {a.notes && <p className="planner__detail-notes">{a.notes}</p>}
                    <label className="planner__field">
                      <span className="planner__field-label">Dag</span>
                      <select
                        className="planner__select"
                        value={a.day === null ? "" : String(a.day)}
                        onChange={(e) => setActivityDay(a.id, e.target.value === "" ? null : Number(e.target.value))}
                      >
                        <option value="">Idéer (ingen dag)</option>
                        {Array.from({ length: dc }, (_, i) => (
                          <option key={i} value={i}>Dag {i + 1}</option>
                        ))}
                      </select>
                    </label>
                    <div className="planner__form-actions">
                      <button
                        className="planner__btn planner__btn--ghost"
                        onClick={() => { removeCard(a); setEditing(null); }}
                      >
                        {inPool ? "Ta bort" : "Till idéer"}
                      </button>
                      <button className="planner__btn" onClick={() => setEditing({ mode: "edit", id: a.id })}>
                        Redigera
                      </button>
                    </div>
                  </div>
                );
              })()
            ) : (
              <ActivityForm
                initial={editing.mode === "edit" ? modalActivity : undefined}
                defaultDay={editing.mode === "new" ? editing.day : null}
                showDaySelect={editing.mode === "edit" || editing.mode === "new"}
                multiDay={editing.mode === "add"}
                dayCount={dc}
                region={baseLat !== null && baseLng !== null ? { lat: baseLat, lng: baseLng } : undefined}
                tripStartISO={startDate}
                onSave={saveActivity}
                onSaveMany={saveActivities}
                onCancel={() =>
                  setEditing(editing.mode === "new" ? { mode: "pick", day: editing.day } : null)
                }
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};
